/**
 * <sunday-ap>: an accessory page's photos and buy box (sections/sunday-ap.liquid), regular and ad landing.
 * Protector, topper: one size button opens the size sheet (width, then length; 84″ only where the store has it).
 * Pillow: Standard / Luxury and How many (1 or 2). The price, the line under it, the EMI line (from the threshold),
 * the Add and the phone bar follow the choice. Start: the mattress in the cart, else ?variant=, else the size the
 * site remembers (@sunday/state "bed"; a custom size takes the standard size it is cut from), else the default.
 * Add goes through @sunday/cart (the piece joins the bed of the same size; a pillow the first bed without one), stays
 * on the page and becomes "− n in cart +" (@sunday/incart), with Go to cart under it.
 * Size known (cart or remembered): one pill with Change. Not known: width tiles and the length row on the page
 * (printed by the section; which one shows is decided before the first paint), same choice, same sheet logic.
 * Photos: like the mattress page: swipe the big picture, a row of thumbnails under it; the film plays only on screen.
 * Delivery: "Free delivery. See your date" opens the pincode check; a remembered answer shows at once.
 */
import { parse, parseAll, uniq, cutFrom } from '@sunday/sizes';
import * as state from '@sunday/state';
import * as cart from '@sunday/cart';
import { paint as paintIn } from '@sunday/incart';
import { money, emi, fill } from '@sunday/util';
import { t } from '@sunday/text';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const WIDTHS = [30, 36, 48, 60, 66, 72];
const LENGTHS = [72, 75, 78, 84];

class SundayAP extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    const c = (this.cfg = JSON.parse($('script[data-ap-cfg]', this).textContent));
    this.w = c.w;
    this.sized = c.kind !== 'pil';
    this.qty = 1;
    this.$add = $('[data-ap-add]', this);
    this.$badd = $('[data-ap-badd]', this);
    this.$err = $('[data-err]', this);
    this.zip = c.kind === 'zip';
    if (this.zip) {
      // 21 Oct (AR 22): zipper cover: your Sunday mattress first (the store's Model option), then its size and thickness.
      this.model = c.model;
      this.all = c.variants.map((v) => { const s = parse(v); if (s) s.model = v.model; return s; }).filter(Boolean);
      this.sizes = this.all.filter((s) => s.model === this.model);
    }
    if (this.sized) {
      if (!this.zip) this.sizes = parseAll(c.variants);
      this.pick = this.start();
    } else {
      this.vid = c.selectedId;
    }
    this.addEventListener('click', (e) => this.click(e));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.open) this.sheet(false);
    });
    document.addEventListener('sunday:cart', (e) => this.inCart(e.detail && e.detail.cart));
    cart.get().then((x) => this.inCart(x)).catch(() => {});
    this.render();
    this.gallery();
    this.bar();
    this.delivery();
  }

  /* ---------- choice ---------- */
  start() {
    // 21 Oct (edge cases): every starting choice is a size in stock (the sold-out ones stay in the sheet, marked).
    const c = this.cfg, S = this.sizes.filter((s) => s.available).length ? this.sizes.filter((s) => s.available) : this.sizes;
    const find = (w, l) => S.find((s) => s.w === w && s.l === l);
    if (this.zip && c.urlHasVariant) {
      const s = S.find((x) => x.id === c.selectedId) || this.sizes.find((x) => x.id === c.selectedId);
      if (s) return { w: s.w, l: s.l, h: s.h };
    }

    if (c.cartSize) {
      const s = find(c.cartSize.w, c.cartSize.l) || cutFrom(S, c.cartSize.w, c.cartSize.l);
      if (s) return { w: s.w, l: s.l, h: s.h };
    }
    if (c.urlHasVariant) {
      const s = S.find((x) => x.id === c.selectedId);
      if (s) return { w: s.w, l: s.l, h: s.h };
    }
    const bed = state.get('bed');
    if (bed && bed.w && bed.l) {
      const s = find(bed.w, bed.l) || cutFrom(S, bed.w, bed.l);
      if (s) return { w: s.w, l: s.l, h: s.h };
    }
    const dm = /^(\d+)\s*x\s*(\d+)$/i.exec(c.defaultSize || '');
    const d = dm && (find(+dm[1], +dm[2]) || (this.zip && (S.find((x) => x.w === +dm[1] && x.l === 75) || S.find((x) => x.w === +dm[1]))));
    const s = d || S.find((x) => x.id === c.selectedId) || S[0];
    return { w: s.w, l: s.l, h: s.h };
  }

  get chosen() {
    if (!this.sized) return this.cfg.variants.find((v) => v.id === this.vid) || this.cfg.variants[0];
    const m = this.sizes.filter((s) => s.w === this.pick.w && s.l === this.pick.l);
    return m.find((s) => s.h === this.pick.h) || m.find((s) => s.available) || m[0] || null;
  }

  heights(w, l) {
    return uniq(this.sizes.filter((s) => s.w === w && s.l === l && s.h).map((s) => s.h));
  }

  /** Zipper cover: keep the size when the new mattress has it, else the nearest in stock. */
  setModel(code) {
    if (!this.zip || code === this.model) return;
    this.model = code;
    this.sizes = this.all.filter((s) => s.model === code);
    const ok = this.sizes.filter((s) => s.available);
    const S = ok.length ? ok : this.sizes;
    const keep = S.find((s) => s.w === this.pick.w && s.l === this.pick.l && s.h === this.pick.h)
      || S.find((s) => s.w === this.pick.w && s.l === this.pick.l)
      || S.find((s) => s.w === this.pick.w)
      || S[0];
    if (keep) this.pick = { w: keep.w, l: keep.l, h: keep.h };
    $$('[data-ap-m]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.apM === code)));
    $$('[data-zm]', this).forEach((el) => { el.hidden = el.dataset.zm !== code; });
    this.url();
  }

  lengths(w) {
    return uniq(this.sizes.filter((s) => s.w === w).map((s) => s.l));
  }

  click(e) {
    const tg = e.target;
    if (tg.closest('[data-ap-size]')) return this.sheet(true);
    const mb = tg.closest('[data-ap-m]');
    if (mb) {
      this.setModel(mb.dataset.apM);
      return this.render();
    }
    const hb = tg.closest('[data-ap-h]');
    if (hb) {
      this.pick = { ...this.pick, h: Number(hb.dataset.apH) };
      this.url();
      return this.render();
    }
    if (tg.closest('[data-ap-close]') || tg.closest('[data-ap-dim]')) return this.sheet(false);
    const w = tg.closest('[data-ap-w]');
    if (w) {
      const nw = Number(w.dataset.apW);
      const ls = this.lengths(nw);
      this.pick = { w: nw, l: ls.includes(this.pick.l) ? this.pick.l : ls.includes(78) ? 78 : ls[0], h: this.pick.h };
      this.remember();
      return this.render();
    }
    const l = tg.closest('[data-ap-l]');
    if (l && !l.disabled) {
      this.pick = { w: this.pick.w, l: Number(l.dataset.apL), h: this.pick.h };
      this.remember();
      return this.render();
    }
    const v = tg.closest('[data-ap-v]');
    if (v) {
      this.vid = Number(v.dataset.apV);
      $$('[data-ap-v]', this).forEach((b) => b.setAttribute('aria-pressed', String(b === v)));
      this.url();
      return this.render();
    }
    const q = tg.closest('[data-ap-q]');
    if (q) {
      this.qty = Number(q.dataset.apQ);
      $$('[data-ap-q]', this).forEach((b) => b.setAttribute('aria-pressed', String(b === q)));
      return this.render();
    }
    const th = tg.closest('[data-ap-thumb]');
    if (th) return this.go(Number(th.dataset.apThumb));
    if (tg.closest('[data-ap-add]') || tg.closest('[data-ap-badd]')) return this.addToCart(tg.closest('[data-ap-add],[data-ap-badd]'));
    if (tg.closest('[data-ap-delbtn]')) {
      $('[data-ap-delq]', this).hidden = true;
      const wdg = $('[data-ap-delw]', this);
      wdg.hidden = false;
      const inp = $('input', wdg);
      if (inp) inp.focus({ preventScroll: true });
    }
  }

  remember() {
    state.set('bed', { w: this.pick.w, l: this.pick.l, from: this.cfg.name });
    this.url();
  }

  url() {
    const v = this.chosen;
    if (!v) return;
    const id = v.id;
    requestAnimationFrame(() => setTimeout(() => {
      const u = new URL(location.href);
      u.searchParams.set('variant', id);
      history.replaceState(history.state, '', u);
    }));
  }

  sizeLabel(v, long) {
    const name = t(`size.${long ? 'w' : 't'}${v.w}`) || v.name;
    return `${name} ${v.l}×${v.w}${this.zip && v.h ? `×${v.h}` : ''}″`;
  }

  /* ---------- paint ---------- */
  render() {
    const c = this.cfg, v = this.chosen;
    if (!v) return;
    /* 24 Oct (release check): "Complete your bed" below follows the size this page shows */
    if (this.sized && !this.zip) document.dispatchEvent(new CustomEvent('sunday:ap-size', { detail: { w: v.w, l: v.l } }));
    const total = v.price * this.qty;
    let label;
    if (this.sized) {
      $('[data-ap-tn]', this).textContent = t(`size.t${v.w}`) || v.name;
      $('[data-ap-lw]', this).textContent = `${v.l}×${v.w}${this.zip && v.h ? `×${v.h}` : ''}″`;
      $('[data-ap-pmeta]', this).textContent = this.w.gst;
      label = this.sizeLabel(v, true);
      this.paintSheet();
      this.paintTiles();
      const $emi = $('[data-ap-emi]', this);
      if ($emi) {
        const on = c.emiMonths > 0 && total >= c.emiFrom;
        $emi.hidden = !on;
        if (on) $emi.textContent = fill(this.w.emi, { amount: money(emi(total, c.emiMonths)) });
      }
    } else {
      const std = c.variants[0] && v.id === c.variants[0].id;
      $('[data-ap-pmeta]', this).textContent = this.qty > 1 ? t('acc2.each', { price: money(v.price) }) : this.w.perPillow;
      const kind = std ? this.w.std : this.w.lux;
      label = this.qty > 1 ? t('acc2.pillows_n', { kind, n: this.qty }) : t('acc2.pillow_1', { kind });
    }
    $('[data-ap-price]', this).textContent = money(total);
    $('[data-ap-bprice]', this).textContent = money(total);
    $('[data-ap-blabel]', this).textContent = label;
    this.label = label;
    for (const b of [this.$add, this.$badd]) {
      if (!b) continue;
      b.disabled = v.available === false;
      // 21 Oct (edge cases): a sold-out choice says so on the button instead of a silent grey one.
      if (b.__sdLbl == null) b.__sdLbl = b.innerHTML;
      if (v.available === false) b.textContent = this.w.soldOut || '';
      else if (b.textContent === this.w.soldOut) b.innerHTML = b.__sdLbl;
      b.dataset.sdV = v.id;
      paintIn(b);
    }
    this.inCart(this.last);
  }

  /* ---------- the size sheet ---------- */
  sheet(open) {
    const sh = $('[data-ap-sheet]', this), dim = $('[data-ap-dim]', this);
    if (!sh) return;
    this.open = open;
    sh.hidden = !open;
    dim.hidden = !open;
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) {
      this.paintSheet();
      this.back = document.activeElement;
      const on = $('[data-ap-w][aria-pressed="true"]', sh);
      (on || $('[data-ap-close]', sh)).focus({ preventScroll: true });
    } else if (this.back) this.back.focus({ preventScroll: true });
  }

  paintSheet() {
    const sh = $('[data-ap-sheet]', this);
    if (!sh || !this.sized) return;
    const ws = WIDTHS.filter((w) => this.sizes.some((s) => s.w === w));
    $('[data-ap-ws]', sh).innerHTML = ws
      .map((w) => `<button type="button" data-ap-w="${w}" aria-pressed="${w === this.pick.w}">${t(`size.t${w}`)}<small>${fill(this.w.wide, { w })}</small></button>`)
      .join('');
    const have = this.lengths(this.pick.w);
    $('[data-ap-ls]', sh).innerHTML = LENGTHS.filter((l) => this.sizes.some((s) => s.l === l))
      .map((l) => `<button type="button" data-ap-l="${l}" aria-pressed="${l === this.pick.l}"${have.includes(l) ? '' : ' disabled'}>${l}″</button>`)
      .join('');
    const v = this.chosen;
    const hs = this.zip ? this.heights(this.pick.w, this.pick.l) : [];
    const $hs = $('[data-ap-hs]', sh), $hl = $('[data-ap-hl]', sh);
    if ($hs) {
      $hs.hidden = $hl.hidden = hs.length < 2;
      $hs.innerHTML = hs.length < 2 ? '' : hs
        .map((h) => `<button type="button" data-ap-h="${h}" aria-pressed="${!!v && v.h === h}">${h}″</button>`)
        .join('');
    }
    if (v) {
      $('[data-ap-sprice]', sh).textContent = money(v.price);
      $('[data-ap-slabel]', sh).textContent = this.sizeLabel(v, true);
    }
  }

  /** 21 Oct (AR 14): the on-page width tiles and length row (only printed when the size is not known). */
  paintTiles() {
    const tw = $$('.sd-ap-szw [data-ap-w]', this);
    if (!tw.length) return;
    tw.forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.apW) === this.pick.w)));
    const have = this.lengths(this.pick.w);
    $$('.sd-ap-lseg [data-ap-l]', this).forEach((b) => {
      const l = Number(b.dataset.apL);
      b.setAttribute('aria-pressed', String(l === this.pick.l));
      b.disabled = !have.includes(l);
    });
  }

  /* ---------- add ---------- */
  async addToCart(btn) {
    const v = this.chosen, c = this.cfg;
    if (!v || this.busy) return;
    this.busy = true;
    this.$err.hidden = true;
    const item = { btn, qty: this.sized ? 1 : this.qty, name: c.name, what: this.label, price: money(v.price * this.qty), image: c.image, tint: 'var(--sd-ground)' };
    document.dispatchEvent(new CustomEvent('sunday:adding', { detail: item }));
    try {
      let res;
      if (!this.sized) {
        const now = await cart.get();
        const bed = cart.beds(now).find((b) => b.key !== 'extras' && b.lines.some(cart.isMattress) && !b.lines.some((l) => Number(l.product_id) === Number(c.productId)));
        const properties = bed ? { _bed: bed.key } : {};
        const after = await cart.addItems([{ id: v.id, quantity: this.qty, properties }]);
        res = { cart: after, bed: properties._bed || null };
      } else {
        res = await cart.addFromProductPage({ variantId: v.id, productId: c.productId, size: { w: v.w, l: v.l } });
      }
      const line = res.cart.items.find((x) => x.variant_id === v.id && ((x.properties || {})._bed || null) === res.bed) || res.cart.items.find((x) => x.variant_id === v.id);
      document.dispatchEvent(new CustomEvent('sunday:added', { detail: { ...item, cart: res.cart, line, stay: true } }));
      this.inCart(res.cart);
    } catch (err) {
      this.$err.textContent = err.message || '';
      this.$err.hidden = false;
      document.dispatchEvent(new CustomEvent('sunday:add-failed', { detail: { btn, message: err.message || '' } }));
    } finally {
      this.busy = false;
    }
  }

  /** "Go to cart" under the Add once this choice is in the cart. */
  inCart(c) {
    if (!c || !c.items) return;
    this.last = c;
    const v = this.chosen;
    const n = v ? c.items.filter((l) => Number(l.variant_id) === Number(v.id)).reduce((a, l) => a + l.quantity, 0) : 0;
    const go = $('[data-ap-go]', this);
    if (go) go.hidden = n === 0;
  }

  /* ---------- photos ---------- */
  gallery() {
    const tr = $('[data-ap-track]', this);
    if (!tr) return;
    const dots = $$('.sd-ap-dots i', this), th = $$('[data-ap-thumb]', this);
    this.g = 0;
    /* 23 Oct (speed): photos after the first wait (data-ap-src, set in sections/sunday-ap.liquid) until the
       visitor reaches for the gallery (touch, swipe, a thumbnail, the keyboard) or a photo scrolls into view.
       Lazy images in a sideways track load at once on a desktop (up to 105 KB nobody saw). */
    const wait = $$('img[data-ap-src]', tr);
    if (wait.length) {
      const load = (im) => {
        if (!im.dataset.apSrc) return;
        if (im.dataset.apSrcset) im.srcset = im.dataset.apSrcset;
        im.src = im.dataset.apSrc;
        delete im.dataset.apSrc; delete im.dataset.apSrcset;
      };
      const all = () => wait.forEach(load);
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.intersectionRatio >= 0.1) { load(e.target); io.unobserve(e.target); } }), { root: tr, threshold: 0.1 }); // 10%: the next slide sits 1 px inside the track's edge
        wait.forEach((im) => io.observe(im));
      } else all();
      ['pointerdown', 'focusin', 'scroll'].forEach((ev) => tr.addEventListener(ev, all, { once: true, passive: true }));
      th.forEach((b) => ['pointerenter', 'focus', 'pointerdown'].forEach((ev) => b.addEventListener(ev, all, { once: true, passive: true })));
    }
    tr.addEventListener('scroll', () => {
      const i = Math.round(tr.scrollLeft / Math.max(1, tr.clientWidth));
      if (i === this.g) return;
      this.g = i;
      dots.forEach((d, k) => d.classList.toggle('sd-on', k === i));
      th.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
    }, { passive: true });
    const film = $('[data-ap-film] video', this);
    if (film && 'IntersectionObserver' in window) {
      new IntersectionObserver((es) => es.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.5) film.play().catch(() => {});
        else film.pause();
      }), { threshold: [0, 0.5, 1] }).observe(film);
    }
  }

  go(i) {
    const tr = $('[data-ap-track]', this);
    tr.scrollTo({ left: i * tr.clientWidth, behavior: 'smooth' });
  }

  /* ---------- phone bar: on once the Add has scrolled away ---------- */
  bar() {
    const bar = $('[data-ap-bar]', this), main = $('.sd-ap-addrow', this);
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    new IntersectionObserver((es) => es.forEach((e) => {
      const show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('sd-on', show);
      bar.inert = !show;
    })).observe(main);
  }

  /* ---------- delivery: a remembered answer shows at once ---------- */
  delivery() {
    const out = $('[data-ap-delw] [data-out]', this);
    if (out && out.dataset.pre) {
      $('[data-ap-delq]', this).hidden = true;
      $('[data-ap-delw]', this).hidden = false;
    }
  }
}

if (!customElements.get('sunday-ap')) customElements.define('sunday-ap', SundayAP);

/**
 * <sunday-apmore>: "Complete your bed." on an accessory page (sections/sunday-acc-bits.liquid, part "more").
 * The other two accessories at the visitor's size (the bed the site remembers, which the buy box above sets); Add
 * stays on the page and becomes "− n in cart +".
 */
/* 21 Oct (edge cases): an Add whose size is sold out: off, and says so (data-sold holds the translated word). */
function soldOut(btn, off) {
  if (!btn) return;
  if (btn.__sdLbl == null) btn.__sdLbl = btn.innerHTML;
  btn.disabled = off;
  if (off) btn.textContent = btn.dataset.sold || '';
  else if (btn.textContent === btn.dataset.sold) btn.innerHTML = btn.__sdLbl;
}

class SundayAPMore extends HTMLElement {
  connectedCallback() {
    if (this.cards) return;
    this.cards = $$('.sd-ab-mc', this).map((el) => ({ el, cfg: JSON.parse($('script[type="application/json"]', el).textContent) }));
    this.$err = $('[data-err]', this);
    this.paint();
    document.addEventListener('sunday:state', (e) => { if (e.detail && e.detail.key === 'bed') this.paint(); });
    document.addEventListener('sunday:ap-size', (e) => { this.apSize = e.detail; this.paint(); });
    const ap = document.querySelector('sunday-ap');
    if (ap && ap.sized && !ap.zip && ap.chosen) { this.apSize = { w: ap.chosen.w, l: ap.chosen.l }; this.paint(); }
    this.addEventListener('click', (e) => {
      const b = e.target.closest('[data-m-add]');
      if (b) this.add(b);
    });
  }

  size() {
    if (this.apSize && this.apSize.w && this.apSize.l) return this.apSize;
    const bed = state.get('bed');
    const [dw, dl] = (this.dataset.default || '60x78').split('x').map(Number);
    return bed && bed.w && bed.l ? { w: bed.w, l: bed.l } : { w: dw, l: dl };
  }

  paint() {
    const z = this.size();
    for (const { el, cfg } of this.cards) {
      const btn = $('[data-m-add]', el);
      if (el.dataset.sized === 'true') {
        // 21 Oct (edge cases): v[5] is variant.available; only a size in stock is offered.
        const sizes = cfg.variants.map((v) => ({ id: v[0], w: v[1], l: v[2], price: v[3], txt: v[4], ok: v[5] !== false })).filter((s) => s.ok);
        const v = sizes.find((s) => s.w === z.w && s.l === z.l) || cutFrom(sizes, z.w, z.l);
        soldOut(btn, !v);
        if (v) {
          btn.dataset.variant = v.id;
          btn.dataset.sdV = v.id;
          $('[data-m-price]', el).textContent = v.txt;
          $('[data-m-unit]', el).textContent = `${t(`size.t${v.w}`)} ${v.l}×${v.w}″`;
        }
      }
      paintIn(btn);
    }
  }

  async add(btn) {
    const el = btn.closest('.sd-ab-mc');
    const { cfg } = this.cards.find((c) => c.el === el);
    const id = Number(btn.dataset.variant);
    const v = cfg.variants.find((x) => x[0] === id);
    const sized = el.dataset.sized === 'true';
    if (!v || btn.getAttribute('aria-busy') === 'true') return;
    this.$err.hidden = true;
    const item = { btn, name: cfg.name, what: $('[data-m-unit]', el).textContent, price: v[4], image: cfg.image, tint: 'var(--sd-ground)' };
    document.dispatchEvent(new CustomEvent('sunday:adding', { detail: item }));
    try {
      const res = await cart.addFromProductPage({ variantId: id, productId: Number(el.dataset.product), size: sized ? { w: v[1], l: v[2] } : null, anyBed: !sized });
      const line = res.cart.items.find((x) => x.variant_id === id) || res.cart.items[0];
      document.dispatchEvent(new CustomEvent('sunday:added', { detail: { ...item, cart: res.cart, line, stay: true } }));
    } catch (e) {
      this.$err.textContent = e.message || '';
      this.$err.hidden = false;
      document.dispatchEvent(new CustomEvent('sunday:add-failed', { detail: { btn, message: e.message || '' } }));
    }
  }
}

if (!customElements.get('sunday-apmore')) customElements.define('sunday-apmore', SundayAPMore);
